import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, AlertCircle, CheckCircle } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { industries } from '@/data/industries';
import { services } from '@/data/services';
import { useMergedIndustry } from '@/hooks/useContentOverrides';

export default function IndustryDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const industry = useMergedIndustry(slug);

  useEffect(() => {
    if (!industry) { navigate('/industries'); return; }
    document.title = `${industry.title} | NKP VENTURES`;
    window.scrollTo(0, 0);
  }, [industry, navigate]);

  if (!industry) return null;
  const Icon = industry.icon;

  // Related industries
  const related = industries.filter(i => i.slug !== slug).slice(0, 3);

  // Relevant services (use first 4)
  const relevantServices = services.slice(0, 4);

  return (
    <div>
      {/* ── Hero + Breadcrumb ── Integrated */}
      <section
        className="relative pt-28 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0D1F30 0%, #153E5C 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.015) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Inline breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-xs text-white/35 font-sans mb-10"
          >
            <Link to="/" className="hover:text-white/60 transition-colors duration-150">Home</Link>
            <span className="text-white/20">/</span>
            <Link to="/industries" className="hover:text-white/60 transition-colors duration-150">Industries</Link>
            <span className="text-white/20">/</span>
            <span className="text-white/55">{industry.title}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/industries"
              className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm mb-8 transition-colors duration-150"
            >
              <ArrowLeft className="w-4 h-4" /> All Industries
            </Link>

            <div className="flex items-start gap-6 mb-8">
              <div className="w-16 h-16 bg-[#0E5A57] ring-1 ring-white/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="overline-label text-[#D4AF37] mb-2">Industry Focus</p>
                <h1 className="font-serif text-4xl lg:text-5xl font-bold text-white leading-tight">
                  {industry.title}
                </h1>
              </div>
            </div>

            <p className="text-white/55 text-lg font-sans leading-relaxed max-w-2xl">{industry.heroSub}</p>
          </motion.div>
        </div>
      </section>

      {/* ── Industry Context ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            <Reveal className="lg:col-span-3">
              <p className="overline-label text-[#0E5A57] mb-4">Industry Context</p>
              <h2 className="font-serif text-3xl font-bold text-[#153E5C] mb-6 leading-tight">
                Understanding your sector
              </h2>
              <p className="text-[#374151] text-base leading-relaxed font-sans">{industry.description}</p>
            </Reveal>

            <Reveal delay={0.12} className="lg:col-span-2">
              <div className="p-8 bg-[#FBF6E3] border border-[#D4AF37]/25 rounded-2xl">
                <p className="overline-label text-[#D4AF37] mb-4">Governance & Standards</p>
                <p className="text-[#374151] text-sm leading-relaxed font-sans">{industry.governanceNote}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Pain Points ── */}
      <section className="py-24 bg-[#F7FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline-label text-[#0E5A57] mb-4">Operational Challenges</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#153E5C] mb-4 max-w-xl leading-tight">
              The challenges we help you solve
            </h2>
            <p className="text-[#6B7280] mb-12 font-sans leading-relaxed max-w-2xl">
              These are the recurring operational issues that businesses in your sector bring to NKP VENTURES.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {industry.painPoints.map((p, i) => (
              <Reveal key={p} delay={i * 0.04}>
                <div className="flex items-start gap-4 p-6 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#D4AF37]/30 transition-colors duration-200">
                  <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#374151] font-sans leading-relaxed">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Support Areas ── Dark */}
      <section className="py-24" style={{ background: '#0D1F30' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-6 h-px bg-[#D4AF37]" />
              <p className="overline-label text-[#D4AF37]">How We Help</p>
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
              Areas of operational support
            </h2>
            <p className="text-white/45 mb-14 font-sans max-w-2xl leading-relaxed">
              Specific functions and capabilities we bring to businesses in this sector.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {industry.supportAreas.map((area, i) => (
              <Reveal key={area} delay={i * 0.04}>
                <div className="group flex items-start gap-4 p-6 border border-white/8 rounded-xl hover:border-white/16 hover:bg-white/4 transition-all duration-300">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-white/75 font-sans leading-relaxed">{area}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why NKP VENTURES ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <p className="overline-label text-[#0E5A57] mb-4">Why NKP VENTURES</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#153E5C] mb-6 leading-tight">
                Built for your industry.
              </h2>
              <p className="text-[#374151] text-base leading-relaxed font-sans mb-8">{industry.whyPkepl}</p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-[#0E5A57] font-semibold text-sm hover:gap-3 transition-all duration-200"
              >
                Schedule a consultation <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="grid grid-cols-2 gap-3">
                {relevantServices.map(s => {
                  const SIcon = s.icon;
                  return (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      className="group flex flex-col gap-3 p-5 bg-[#F7FAFC] border border-[#E5E7EB] rounded-xl hover:border-[#0E5A57]/25 hover:bg-white hover:shadow-md transition-all duration-200"
                    >
                      <div className="w-9 h-9 bg-white border border-[#E5E7EB] rounded-lg flex items-center justify-center group-hover:bg-[#0E5A57] group-hover:border-[#0E5A57] transition-all duration-300">
                        <SIcon className="w-4 h-4 text-[#0E5A57] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <p className="text-xs font-semibold text-[#153E5C] leading-snug group-hover:text-[#0E5A57] transition-colors duration-200">{s.shortTitle}</p>
                    </Link>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Related Industries ── */}
      <section className="py-16 bg-[#F7FAFC] border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline-label text-[#0E5A57] mb-8">Explore More Industries</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map(ind => {
                const RelIcon = ind.icon;
                return (
                  <Link
                    key={ind.slug}
                    to={`/industries/${ind.slug}`}
                    className="group flex items-center gap-4 p-5 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#153E5C]/25 hover:shadow-md transition-all duration-200"
                  >
                    <div className="w-10 h-10 bg-[#F7FAFC] border border-[#E5E7EB] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#153E5C] group-hover:border-[#153E5C] transition-all duration-300">
                      <RelIcon className="w-5 h-5 text-[#153E5C] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#153E5C] group-hover:text-[#0E5A57] transition-colors duration-200 leading-snug">{ind.shortTitle}</p>
                      <p className="text-xs text-[#9CA3AF] mt-0.5 truncate">{ind.heroSub}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#D1D5DB] group-hover:text-[#0E5A57] flex-shrink-0 transition-colors duration-200" />
                  </Link>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 bg-[#153E5C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="overline-label text-teal-400 mb-4">Ready to Discuss Your Needs?</p>
                <h2 className="font-serif text-4xl font-bold text-white mb-4 leading-tight">
                  Talk to our {industry.shortTitle}<br />operations team.
                </h2>
                <p className="text-white/60 font-sans leading-relaxed max-w-md">
                  Schedule a consultation to explore how NKP VENTURES can support your operations.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <Link to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#153E5C] font-bold px-9 py-4 rounded-full transition-all duration-200 group">
                  Schedule a Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
                <Link to="/industries"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white font-medium px-9 py-4 rounded-full transition-all duration-200 text-sm">
                  <ArrowLeft className="w-4 h-4" /> All Industries
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
